import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight, Users, Sparkles } from 'lucide-react';
import { useCommunityLinks } from '../../hooks/useCommunityLinks';
import type { CommunityLink } from '../../hooks/useApi';

const WA_GREEN = '#25D366';
const WA_GREEN_DEEP = '#128C7E';

/** Resmi WhatsApp glifi (marka tanınırlığı için inline SVG). */
export const WhatsAppGlyph: React.FC<{ size?: number; color?: string }> = ({
  size = 22,
  color = '#fff',
}) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill={color} aria-hidden="true">
    <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.46 1.74 6.4L3.2 28.8l6.58-1.72a12.74 12.74 0 0 0 6.22 1.6h.01c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.33-6.64-3.75-9.06A12.71 12.71 0 0 0 16.004 3.2Zm0 23.36h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-4.02 1.05 1.07-3.92-.25-.4a10.56 10.56 0 0 1-1.62-5.63c0-5.86 4.77-10.62 10.63-10.62 2.84 0 5.5 1.11 7.51 3.12a10.55 10.55 0 0 1 3.11 7.51c0 5.86-4.77 10.62-10.63 10.62Zm5.83-7.96c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.74-.99-2.38-.26-.62-.52-.54-.72-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66s1.15 3.09 1.31 3.3c.16.21 2.26 3.45 5.48 4.84.76.33 1.36.53 1.83.68.77.24 1.47.21 2.02.13.62-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37Z" />
  </svg>
);

function LinkRow({ link, index }: { link: CommunityLink; index: number }) {
  return (
    <motion.a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 18, filter: 'blur(6px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ delay: 0.12 + index * 0.07, type: 'spring', stiffness: 260, damping: 24 }}
      whileHover={{ y: -4, scale: 1.015 }}
      whileTap={{ scale: 0.985 }}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '16px 18px',
        borderRadius: '16px',
        textDecoration: 'none',
        background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.10), rgba(18, 140, 126, 0.06))',
        border: '1px solid rgba(37, 211, 102, 0.22)',
        overflow: 'hidden',
      }}
      className="wa-link-row"
    >
      {/* Hover parıltı süpürmesi */}
      <span className="wa-sheen" aria-hidden="true" />
      <motion.span
        style={{
          flexShrink: 0,
          width: '46px',
          height: '46px',
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${WA_GREEN}, ${WA_GREEN_DEEP})`,
          boxShadow: '0 6px 18px rgba(37, 211, 102, 0.35)',
        }}
        whileHover={{ rotate: [0, -8, 8, 0] }}
        transition={{ duration: 0.5 }}
      >
        <WhatsAppGlyph size={24} />
      </motion.span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#f0fdf4', fontSize: '15px', lineHeight: 1.3 }}>
          {link.label}
        </div>
        {link.description ? (
          <div
            style={{
              color: '#86efac',
              fontSize: '12.5px',
              marginTop: '3px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {link.description}
          </div>
        ) : (
          <div style={{ color: '#4ade8099', fontSize: '12.5px', marginTop: '3px' }}>
            Katılmak için dokun
          </div>
        )}
      </div>

      <motion.span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: '34px',
          height: '34px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(37, 211, 102, 0.14)',
          color: WA_GREEN,
        }}
      >
        <ArrowUpRight size={18} />
      </motion.span>
    </motion.a>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        padding: '32px 20px',
        textAlign: 'center',
        borderRadius: '16px',
        border: '1px dashed rgba(37, 211, 102, 0.3)',
        background: 'rgba(37, 211, 102, 0.04)',
      }}
    >
      <Users size={30} color={WA_GREEN} style={{ marginBottom: '10px', opacity: 0.8 }} />
      <p style={{ color: '#86efac', margin: 0, fontSize: '14px' }}>
        Topluluk bağlantıları çok yakında burada olacak.
      </p>
    </div>
  );
}

/** Navbar butonu ve giriş yapan kullanıcı pop-up'ı için animasyonlu modal. */
export const WhatsAppCommunityModal: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const { links, loading } = useCommunityLinks();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            background: 'rgba(3, 7, 18, 0.72)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 30 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            className="wa-modal-card"
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '480px',
              maxHeight: '86vh',
              overflowY: 'auto',
              borderRadius: '24px',
              padding: '2px',
              background:
                'linear-gradient(140deg, rgba(37, 211, 102, 0.55), rgba(18, 140, 126, 0.15), rgba(37, 211, 102, 0.4))',
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6), 0 0 60px rgba(37, 211, 102, 0.15)',
            }}
          >
            <div
              style={{
                position: 'relative',
                borderRadius: '22px',
                padding: '26px 22px 24px',
                background: 'linear-gradient(180deg, #071b12 0%, #04101a 100%)',
                overflow: 'hidden',
              }}
            >
              {/* Dekoratif yüzen ışık kürelerı */}
              <motion.div
                aria-hidden="true"
                animate={{ y: [0, -14, 0], x: [0, 8, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: '-30px',
                  width: '160px',
                  height: '160px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(37, 211, 102, 0.28), transparent 70%)',
                  filter: 'blur(4px)',
                  pointerEvents: 'none',
                }}
              />
              <motion.div
                aria-hidden="true"
                animate={{ y: [0, 12, 0], x: [0, -10, 0] }}
                transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  bottom: '-50px',
                  left: '-40px',
                  width: '170px',
                  height: '170px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(18, 140, 126, 0.24), transparent 70%)',
                  pointerEvents: 'none',
                }}
              />

              <button
                type="button"
                onClick={onClose}
                aria-label="Kapat"
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '34px',
                  height: '34px',
                  borderRadius: '10px',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  background: 'rgba(15, 23, 42, 0.5)',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <X size={18} />
              </button>

              {/* Başlık */}
              <div style={{ position: 'relative', textAlign: 'center', marginBottom: '22px' }}>
                <motion.div
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.05 }}
                  style={{
                    width: '68px',
                    height: '68px',
                    margin: '0 auto 14px',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${WA_GREEN}, ${WA_GREEN_DEEP})`,
                    boxShadow: '0 10px 30px rgba(37, 211, 102, 0.45)',
                  }}
                  className="wa-pulse-badge"
                >
                  <WhatsAppGlyph size={34} />
                </motion.div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: '1.35rem',
                    fontWeight: 800,
                    color: '#f0fdf4',
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  WhatsApp Topluluklarımız
                </h2>
                <p
                  style={{
                    margin: '6px 0 0',
                    color: '#86efac',
                    fontSize: '13.5px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Sparkles size={14} /> Sohbete katıl, paylaş, birlikte öğren
                </p>
              </div>

              {/* Bağlantılar */}
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '28px 0' }}>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      style={{
                        width: '28px',
                        height: '28px',
                        margin: '0 auto',
                        borderRadius: '50%',
                        border: `3px solid rgba(37, 211, 102, 0.25)`,
                        borderTopColor: WA_GREEN,
                      }}
                    />
                  </div>
                ) : links.length === 0 ? (
                  <EmptyState />
                ) : (
                  links.map((link, i) => <LinkRow key={`${link.href}-${i}`} link={link} index={i} />)
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/** Anasayfada gösterilen herkese açık WhatsApp topluluk bölümü. */
export const WhatsAppCommunitySection: React.FC = () => {
  const { links, loading } = useCommunityLinks();

  if (!loading && links.length === 0) return null;

  return (
    <section
      id="whatsapp-topluluk"
      style={{ position: 'relative', padding: '48px 20px', maxWidth: '1100px', margin: '0 auto' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        style={{ textAlign: 'center', marginBottom: '32px' }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 14px',
            borderRadius: '999px',
            background: 'rgba(37, 211, 102, 0.12)',
            border: '1px solid rgba(37, 211, 102, 0.3)',
            color: WA_GREEN,
            fontSize: '12.5px',
            fontWeight: 700,
            marginBottom: '14px',
          }}
        >
          <WhatsAppGlyph size={15} color={WA_GREEN} /> Topluluk
        </span>
        <h2
          style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.1rem)',
            fontWeight: 800,
            margin: '0 0 8px',
            fontFamily: "'Space Grotesk', sans-serif",
            color: '#f8fafc',
          }}
        >
          WhatsApp Topluluklarımıza Katıl
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '15px', maxWidth: '520px', margin: '0 auto', lineHeight: 1.6 }}>
          Notları paylaş, sorularını sor ve binlerce öğrenciyle anlık iletişimde kal. Giriş yapmana
          gerek yok — dilediğin gruba hemen katıl.
        </p>
      </motion.div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '14px',
        }}
      >
        {(loading ? Array.from({ length: 3 }) : links).map((link: any, i: number) =>
          loading ? (
            <div
              key={i}
              style={{
                height: '78px',
                borderRadius: '16px',
                background: 'rgba(37, 211, 102, 0.06)',
                border: '1px solid rgba(37, 211, 102, 0.12)',
              }}
              className="wa-skeleton"
            />
          ) : (
            <LinkRow key={`${link.href}-${i}`} link={link} index={i} />
          )
        )}
      </div>
    </section>
  );
};
